import { FilterMatchMode, FilterService } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DataTableSkeleton from '../SectionSkeletons/DataTableSkeleton';
import { DataModalProps, SectionType } from './DataModal.interface';
import './DataModal.styles.scss';

export type { SectionType };

const sectionLabels: Record<SectionType, string> = {
  contractExpirations: 'Contract Expirations',
  andaUpdates: 'Recent ANDA Updates',
  preSolicitationAlerts: 'Solicitation Alerts',
  lossOfExclusivity: 'Loss of Exclusivity',
};

/**
 * Parse a date string in various formats
 * Supports: "MM/DD/YYYY", "YYYY-MM-DD", "Month DD, YYYY", "DD-Mon-YYYY", etc.
 */
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const trimmed = dateStr.trim();

  // Try native Date parsing first (handles ISO, "Month DD, YYYY", etc.)
  let parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }

  // Try MM/DD/YYYY or M/D/YYYY format
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Try DD-MM-YYYY or D-M-YYYY format
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const [, day, month, year] = dashMatch;
    parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Try "DD Mon YYYY" or "DD-Mon-YYYY" format (e.g., "15 Dec 2025", "15-Dec-2025")
  const monthNames: Record<string, number> = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };

  const monthMatch = trimmed.match(/^(\d{1,2})[\s-]([a-zA-Z]+)[\s-](\d{4})$/);
  if (monthMatch) {
    const [, day, monthStr, year] = monthMatch;
    const monthIndex = monthNames[monthStr.toLowerCase()];
    if (monthIndex !== undefined) {
      parsed = new Date(parseInt(year), monthIndex, parseInt(day));
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  return null;
};

// Generic date range filter function
const dateRangeFilterFunction = (
  value: string | Date | null | undefined,
  filters: [Date | null, Date | null] | null
) => {
  const [from, to] = filters ?? [null, null];
  if (from === null && to === null) return true;

  // Handle string dates with robust parsing
  let valueDate: Date | null = null;
  if (value) {
    if (typeof value === 'string') {
      valueDate = parseDate(value);
    } else if (value instanceof Date) {
      valueDate = value;
    }
  }

  // If we can't parse the date, don't filter it out - show it
  if (!valueDate || isNaN(valueDate.getTime())) return true;

  if (from !== null && to === null) {
    const fromDate = new Date(from);
    fromDate.setHours(0, 0, 0, 0);
    valueDate.setHours(0, 0, 0, 0);
    return fromDate <= valueDate;
  }
  if (from === null && to !== null) {
    const toDate = new Date(to);
    toDate.setHours(23, 59, 59, 999);
    valueDate.setHours(23, 59, 59, 999);
    return valueDate <= toDate;
  }

  const fromDate = new Date(from);
  fromDate.setHours(0, 0, 0, 0);
  const toDate = new Date(to);
  toDate.setHours(23, 59, 59, 999);
  valueDate.setHours(0, 0, 0, 0);

  return fromDate <= valueDate && valueDate <= toDate;
};

const DataModal: React.FC<DataModalProps> = ({
  visible,
  onHide,
  data,
  sectionType,
  columns,
  onSectionChange,
  sectionDropdownConfigs,
  isLoading = false,
  onRowClick,
  onFiltersChange,
}) => {
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  // Track which section filters were initialized for to prevent resetting on re-renders
  const initializedSectionRef = useRef<SectionType | null>(null);

  interface ProcessedRowData {
    tags?: Array<{
      earliest_possible_launch_date?: string;
      earliest_launch_probability?: string;
      triggering_event?: string;
      type?: string;
      text?: string;
    }>;
    title?: string;
    brand_name?: string;
    brand_company?: string;
    tags_launchDate?: string;
    tags_launchProbability?: string;
    tags_competitor?: string;
    tags_type?: string;
    tags_agency?: string;
    [key: string]: unknown;
  }

  /**
   * Preprocess data to extract nested tag values into top-level properties
   * that match the column field names for proper filtering support.
   * This fixes the issue where filtering didn't work for columns that render
   * values from nested tags array (e.g., tags_launchDate, tags_launchProbability, tags_competitor).
   */
  const preprocessDataForFiltering = (
    rawData: ProcessedRowData[],
    section: SectionType
  ): ProcessedRowData[] => {
    if (section !== 'lossOfExclusivity' && section !== 'preSolicitationAlerts') {
      return rawData;
    }

    return rawData.map(row => {
      const processedRow = { ...row };

      if (section === 'lossOfExclusivity') {
        // Extract brand name and brand company from title if not already present
        // Title format: "Generic Name - Brand Name - Brand Company"
        if (row.title && !row.brand_name) {
          const titleParts = row.title.split(' - ');
          processedRow.brand_name = titleParts[1] || '';
        }
        if (row.title && !row.brand_company) {
          const titleParts = row.title.split(' - ');
          // Join remaining parts in case company name contains ' - '
          processedRow.brand_company = titleParts.slice(2).join(' - ') || '';
        }

        // Extract launch date from tags
        if (row.tags) {
          const launchDateTag = row.tags.find(tag => tag.earliest_possible_launch_date);
          if (launchDateTag?.earliest_possible_launch_date) {
            processedRow.tags_launchDate = launchDateTag.earliest_possible_launch_date.replace(
              /^Launch:\s*/,
              ''
            );
          }

          // Extract launch probability from tags
          const launchProbTag = row.tags.find(tag => tag.earliest_launch_probability);
          if (launchProbTag?.earliest_launch_probability) {
            processedRow.tags_launchProbability =
              launchProbTag.earliest_launch_probability.split(' ')[0];
          }

          // Extract competitor/triggering event from tags
          const competitorTag = row.tags.find(tag => tag.triggering_event);
          if (competitorTag?.triggering_event) {
            processedRow.tags_competitor = competitorTag.triggering_event;
          }
        }
      }

      if (section === 'preSolicitationAlerts' && row.tags) {
        // Extract type from tags
        const typeTag = row.tags.find(tag => tag.type);
        if (typeTag?.type) {
          processedRow.tags_type = typeTag.type;
        }

        // Extract agency from tags (second element)
        if (row.tags[1]?.text) {
          processedRow.tags_agency = row.tags[1].text;
        }
      }

      return processedRow;
    });
  };

  // Preprocess the data for filtering
  const processedData = preprocessDataForFiltering(data, sectionType);

  // Inject the current concept_name filter term into every row so that when the
  // term changes, row objects change → PrimeReact re-renders existing cells.
  // Without this, PrimeReact reuses cached cell output for unchanged rows and
  // the highlight closure never fires for them.
  const conceptFilterTerm =
    (filters['concept_name'] as { value: string } | undefined)?.value ?? '';
  const ndcFilterTerm =
    (filters['ndcs'] as { value: string } | undefined)?.value ?? '';

  // Inject filter terms into every row so PrimeReact re-renders existing cells
  // when the terms change (PrimeReact caches cell output keyed by row data).
  const finalData = useMemo(
    () =>
      processedData.map(row => ({
        ...row,
        _conceptFilterTerm: conceptFilterTerm,
        _ndcFilterTerm: ndcFilterTerm,
      })),
    [processedData, conceptFilterTerm, ndcFilterTerm]
  );

  // Initialize filters based on columns - only when section actually changes
  useEffect(() => {
    // Skip re-initialization if filters were already set up for this section
    if (initializedSectionRef.current === sectionType) {
      return;
    }

    const initialFilters: Record<string, { value: unknown; matchMode: string }> = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    // Register the date range filter once with a generic name
    FilterService.register('dateRange', dateRangeFilterFunction);

    columns.forEach(column => {
      if (column.filterable !== false) {
        const isDateColumn = column.dataType === 'date' || column.field === 'date';
        if (isDateColumn) {
          // Use the registered 'dateRange' filter for date columns
          initialFilters[column.field] = { value: null, matchMode: 'dateRange' };
        } else {
          initialFilters[column.field] = { value: null, matchMode: FilterMatchMode.CONTAINS };
        }
      }
    });

    setFilters(initialFilters);
    initializedSectionRef.current = sectionType;
  }, [columns, sectionType]);

  const handleSectionClick = (section: SectionType) => {
    if (onSectionChange) {
      onSectionChange(section);
    }
  };

  const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
    return path.split('.').reduce((acc: unknown, part: string) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj);
  };

  const dateRangeFilterTemplate = (options: {
    value: [Date | null, Date | null] | null;
    filterApplyCallback: (value: [Date | null, Date | null]) => void;
  }) => {
    const [from, to] = options.value ?? [null, null];

    return (
      <div
        className="flex gap-1"
        style={{ display: 'flex', gap: '10px', flexDirection: 'row', width: '100%' }}
      >
        <Calendar
          value={from}
          onChange={e => options.filterApplyCallback([e.value, to])}
          placeholder="From"
          dateFormat="mm/dd/yy"
          showIcon
          className="p-column-filter"
          style={{ width: '10rem' }}
        />
        <Calendar
          value={to}
          onChange={e => options.filterApplyCallback([from, e.value])}
          placeholder="To"
          dateFormat="mm/dd/yy"
          showIcon
          className="p-column-filter"
          style={{ width: '10rem' }}
        />
      </div>
    );
  };

  // Get dropdown config for current section
  const currentDropdownConfig = sectionDropdownConfigs?.[sectionType];

  // Reset initialized section ref when modal is closed
  // This allows filters to be re-initialized when modal is opened again
  useEffect(() => {
    if (!visible) {
      initializedSectionRef.current = null;
    }
  }, [visible]);

  // Ensure dropdown panel has high z-index when modal is visible
  useEffect(() => {
    if (!visible) return;

    const applyDropdownZIndex = () => {
      // Find dropdown panels that belong ONLY to our modal dropdown
      // Using unique class name to ensure we don't affect other components
      const dropdownPanels = document.querySelectorAll('.data-modal-dropdown-panel');

      // Apply z-index ONLY to panels with our unique class (this component only)
      dropdownPanels.forEach(panel => {
        // Double-check it has our unique class before applying
        if (panel.classList.contains('data-modal-dropdown-panel')) {
          (panel as HTMLElement).style.zIndex = '99999';
        }
      });

      // Find wrappers and ONLY apply z-index if they contain our unique panel class
      const dropdownWrappers = document.querySelectorAll('.p-dropdown-panel-wrapper');
      dropdownWrappers.forEach(wrapper => {
        // Only modify if wrapper contains our unique class - won't affect other dropdowns
        if (wrapper.querySelector('.data-modal-dropdown-panel')) {
          (wrapper as HTMLElement).style.zIndex = '99999';
        }
      });
    };

    // Apply immediately
    applyDropdownZIndex();

    // Use MutationObserver to watch for dropdown panel creation/updates
    const observer = new MutationObserver(mutations => {
      let shouldApply = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (
              node instanceof HTMLElement &&
              (node.classList.contains('p-dropdown-panel-wrapper') ||
                node.classList.contains('data-modal-dropdown-panel') ||
                node.querySelector('.data-modal-dropdown-panel'))
            ) {
              shouldApply = true;
            }
          });
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (
            target.classList.contains('p-dropdown-panel-wrapper') ||
            target.classList.contains('data-modal-dropdown-panel')
          ) {
            shouldApply = true;
          }
        }
      });
      if (shouldApply) {
        applyDropdownZIndex();
      }
    });

    // Observe document body for dropdown panel additions
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [visible, sectionType]);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={null}
      className="data-modal"
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      style={{ width: '110vw', zoom: 0.75 }}
    >
      <div className="data-modal-content">
        {/* Modal Header with Dropdown and Section Tabs */}
        <div className="modal-header-container">
          {currentDropdownConfig && (
            <div className="modal-dropdown-container">
              <Dropdown
                value={currentDropdownConfig.value}
                options={currentDropdownConfig.options}
                onChange={currentDropdownConfig.onChange}
                placeholder={currentDropdownConfig.placeholder || 'Select Option'}
                className="modal-dropdown data-modal-dropdown"
                optionLabel="label"
                optionValue="value"
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
                panelClassName="data-modal-dropdown-panel"
              />
            </div>
          )}
          {/* Section Tabs */}
          <div className="section-tabs">
            {(Object.keys(sectionLabels) as SectionType[]).map(section => (
              <button
                key={section}
                className={`section-tab ${sectionType === section ? 'active' : ''}`}
                onClick={() => handleSectionClick(section)}
              >
                {sectionLabels[section]}
              </button>
            ))}
          </div>
        </div>

        {/* Data Table */}
        <div className={`data-table-container`}>
          {isLoading ? (
            <DataTableSkeleton columns={columns} rowCount={6} />
          ) : (
            <div className="datatable-wrapper">
              <DataTable
                value={finalData}
                filters={filters}
                onFilter={e => {
                  setFilters(e.filters);
                  onFiltersChange?.(e.filters as Record<string, unknown>);
                }}
                filterDisplay="row"
                globalFilterFields={columns
                  .filter(col => col.filterable !== false)
                  .map(col => col.field)}
                emptyMessage={`No data available for ${sectionLabels[sectionType]}`}
                rowClassName={onRowClick ? () => 'cursor-pointer' : undefined}
                className={`data-table-datatable`}
                scrollable
                scrollHeight="calc(80vh - 200px)"
                selectionMode={onRowClick ? 'single' : undefined}
                metaKeySelection={false}
                onRowClick={
                  onRowClick
                    ? (e: DataTableRowClickEvent) => {
                        // Prevent triggering row click when clicking on filter inputs or buttons
                        const target = e.originalEvent?.target as HTMLElement;
                        if (
                          target?.tagName === 'INPUT' ||
                          target?.tagName === 'BUTTON' ||
                          target?.closest('input') ||
                          target?.closest('button') ||
                          target?.closest('.p-column-filter')
                        ) {
                          return;
                        }
                        onRowClick(e.data as Record<string, unknown>);
                      }
                    : undefined
                }
              >
                {columns.map((column, index) => {
                  const isDateColumn = column.dataType === 'date' || column.field === 'date';
                  const isFilterable = column.filterable !== false;

                  return (
                    <Column
                      key={index}
                      field={column.field}
                      header={column.header}
                      style={{ width: column.width }}
                      body={rowData => {
                        const value = getNestedValue(rowData, column.field);

                        // Use column render function if available
                        if (column.render) {
                          try {
                            // Read filter terms from row data (not the closure) so
                            // PrimeReact cell caching doesn't suppress updates.
                            const filterVal =
                              column.field === 'concept_name'
                                ? (rowData['_conceptFilterTerm'] as string | undefined) ?? ''
                                : column.field === 'ndcs'
                                ? (rowData['_ndcFilterTerm'] as string | undefined) ?? ''
                                : (filters[column.field] as { value: string } | undefined)
                                    ?.value ?? '';
                            const rendered = column.render(value, rowData, filterVal);
                            // Ensure React elements are properly rendered
                            return rendered;
                          } catch (_error) {
                            return value || '-';
                          }
                        }

                        // Fallback: Apply truncation for description column in all sections
                        if (column.field === 'description' && value) {
                          const maxLength = 100;
                          const stringValue = String(value);
                          if (stringValue.length > maxLength) {
                            const truncated = stringValue.substring(0, maxLength) + '...';
                            return <span title={stringValue}>{truncated}</span>;
                          }
                        }

                        // Default: return value as-is
                        return value || '-';
                      }}
                      filter={isFilterable}
                      filterPlaceholder={isDateColumn ? 'Date Range' : `Search ${column.header}`}
                      filterElement={
                        isDateColumn && isFilterable ? dateRangeFilterTemplate : undefined
                      }
                      showFilterMenu={false}
                      showClearButton={true}
                      filterMatchMode={
                        isDateColumn ? ('dateRange' as FilterMatchMode) : FilterMatchMode.CONTAINS
                      }
                    />
                  );
                })}
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default DataModal;
